import React from 'react';
import { Bot, Zap, BarChart3, RefreshCw, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const AIAutomation = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const benefits = [
    {
      icon: Zap,
      title: 'Cut the Busywork',
      description: "Your team didn't sign up to copy data between spreadsheets or chase the same emails every week. We set up automations that handle the repetitive stuff, so your people spend time on work that actually matters.",
    },
    {
      icon: BarChart3,
      title: 'Make Sense of What You Already Have',
      description: "Most businesses are sitting on useful data they never look at. We help you pull out the numbers that count — clear reports, real trends, no 50-page documents nobody reads.",
    },
    {
      icon: RefreshCw,
      title: 'Speed Things Up Without the Faff',
      description: "From quoting to customer follow-ups, there are processes in every business that take longer than they should. We find the bottlenecks and fix them with tools that work quietly in the background.",
    },
    {
      icon: Bot,
      title: "AI That's Actually Useful",
      description: "We're not here to sell you a chatbot for the sake of it. If AI can save you time or money, we'll show you where and set it up properly. If it can't, we'll tell you that too.",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50" data-testid="ai-automation-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 border border-violet-200 rounded-full text-violet-700 text-sm font-medium mb-6">
              <Bot className="w-4 h-4" />
              New Service
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              AI & Automation
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-blue-600">for Real Businesses</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              Every business has tasks that eat up time — chasing updates, re-keying information, pulling together reports nobody asked for. AI and automation won't replace your team, but they can take the dull stuff off their plate so they can get on with the work that actually moves the business forward.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              We're not interested in buzzwords or selling you technology for the sake of it. We look at how your business actually works, find the bits that are slower or more manual than they need to be, and put simple, practical tools in place to fix them. No jargon, no over-engineering — just things that work and make a noticeable difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={scrollToContact}
                className="bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-5 rounded-lg shadow-lg shadow-violet-600/30 hover:shadow-violet-600/40 transition-all"
              >
                Let's Talk AI <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Right — benefit cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((b) => (
              <Card key={b.title} className="border border-slate-200 hover:border-violet-400 hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="p-2.5 bg-gradient-to-br from-violet-50 to-blue-50 rounded-xl inline-block mb-3 group-hover:from-violet-100 group-hover:to-blue-100 transition-colors">
                    <b.icon className="w-6 h-6 text-violet-600" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{b.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIAutomation;
